import React, { useMemo } from 'react';
import { Button, Card, Empty, Typography } from 'antd';
import AreaMap from '../Map/AreaMap';
import TrendIndicator, { TrendDirection } from './TrendIndicator';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import type { Dayjs } from 'dayjs';
import { formatMeasurementTimestamp } from '../../utils/measurementTime';

const { Title, Text } = Typography;

type TrendDir = TrendDirection;

export type MeasurementSummary = {
  id: number;
  takenAt: string;
  measuredTimezone?: string | null;
  measuredUtcOffsetMinutes?: number | null;
  timeSource?: string | null;
  timezoneSource?: string | null;
  lat: number | null;
  lng: number | null;
  areaId: number | null;
  areaName: string | null;
  areaPolygon: any;
  areaType?: { id: number; name: string; templateImage?: string | null; templateLinesImage?: string | null } | null;
  measurementCode: string;
  metrics: Record<string, number | null>;
  trends?: Record<string, { direction: TrendDir; previous?: number | null }>;
};

type MetricCard = {
  key: string;
  label: string;
  value: string;
  trend: TrendDir;
  positiveIsGood: boolean;
};

const DEFAULT_VIEW = {
  longitude: -0.1278,
  latitude: 51.5074,
  zoom: 13,
};

export default function MeasurementSummaryCard({
  title = 'Measurement',
  measurement,
  templateImageUrl,
  metricsToShow,
  onMeasurementClick,
  onAreaClick,
  showReportCta = false,
  reportDateRange,
  dayMeasurements = [],
  areaId,
  emptyDescription = 'No measurements are available.',
}: {
  title?: React.ReactNode;
  measurement: MeasurementSummary | null;
  templateImageUrl?: string | null;
  metricsToShow: MetricCard[];
  onMeasurementClick?: (measurementId: string | number, areaId?: number | null, rangeOverride?: [Dayjs, Dayjs]) => void;
  onAreaClick?: (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => void;
  showReportCta?: boolean;
  reportDateRange?: [Dayjs, Dayjs] | null;
  dayMeasurements?: Array<{
    id: number;
    lat: number | null;
    lng: number | null;
    measurementCode: string | null;
  }>;
  areaId?: number | null;
  emptyDescription?: React.ReactNode;
}) {
  const latest = measurement;
  const resolvedAreaId = areaId ?? latest?.areaId ?? null;
  const areaType = latest?.areaType ?? null;

  const viewState = useMemo(() => {
    if (typeof latest?.lat === 'number' && typeof latest?.lng === 'number') {
      return { longitude: latest.lng, latitude: latest.lat, zoom: 15 };
    }
    if (latest?.areaPolygon?.coordinates?.length) {
      const poly = latest.areaPolygon;
      if (poly.type === 'Point') {
        const [pLng, pLat] = poly.coordinates as number[];
        return { longitude: pLng, latitude: pLat, zoom: 14 };
      }
      const coords: any[] = poly.coordinates[0];
      if (!Array.isArray(coords) || !coords.length) return DEFAULT_VIEW;
      const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
      const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
      return { longitude: lon, latitude: lat, zoom: 14 };
    }
    return DEFAULT_VIEW;
  }, [latest]);

  const mapMeasurements = useMemo(() => {
    // If we have dayMeasurements, use those instead of just the single latest measurement
    if (dayMeasurements.length > 0) {
      return dayMeasurements
        .filter(m => typeof m.lat === 'number' && typeof m.lng === 'number')
        .map(m => ({
          lat: m.lat!,
          lng: m.lng!,
          id: m.id,
          label: m.measurementCode || `Measurement ${m.id}`
        }));
    }
    
    // Fallback to single latest measurement if no dayMeasurements
    if (typeof latest?.lat === 'number' && typeof latest?.lng === 'number') {
      return [{ lat: latest.lat, lng: latest.lng, id: latest.id, label: latest.measurementCode }];
    }
    
    return [];
  }, [latest, dayMeasurements]);

  const resolvedTemplateImageUrl = useMemo(() => {
    if (templateImageUrl) return templateImageUrl;
    if (areaType?.id && areaType?.templateImage) {
      return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-image?t=${Date.now()}`;
    }
    if (areaType?.id && areaType?.templateLinesImage) {
      return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-lines-image?t=${Date.now()}`;
    }
    return null;
  }, [templateImageUrl, areaType?.id, areaType?.templateImage, areaType?.templateLinesImage]);

  const hasArea = Boolean(resolvedAreaId);
  const canNavigateArea = hasArea && !!onAreaClick;
  const canNavigateMeasurement = Boolean(latest?.id && onMeasurementClick);
  const ctaLabel = hasArea ? 'View area report' : 'View measurement report';
  const handleCtaClick = () => {
    if (canNavigateArea) {
      onAreaClick?.(resolvedAreaId as number, reportDateRange ?? undefined);
      return;
    }
    if (canNavigateMeasurement && latest?.id) {
      onMeasurementClick?.(latest.id, resolvedAreaId ?? null, reportDateRange ?? undefined);
    }
  };

  return (
    <Card className="dashboard-map-card" title={title}>
      {latest ? (
        <div className="dashboard-map-grid">
          <div className="dashboard-map-wrapper">
            {((typeof latest.lat === 'number' && typeof latest.lng === 'number') || latest.areaPolygon) ? (
              <AreaMap
                mapKey={latest.id}
                areaGeometry={latest.areaPolygon}
                areaName={latest.areaName || undefined}
                measurements={mapMeasurements}
                initialViewState={viewState}
                areaType={areaType}
                templateImageUrl={resolvedTemplateImageUrl}
                onMeasurementClick={
                  onMeasurementClick
                    ? (measurementId) => onMeasurementClick(measurementId, resolvedAreaId, reportDateRange ?? undefined)
                    : undefined
                }
              />
            ) : (
              <Empty description="No location for this measurement." />
            )}
          </div>

          <div className="dashboard-latest-panel">
            <Text type="secondary">Area</Text>
            <Title level={4}>{latest.areaName || 'Unassigned area'}</Title>

            {latest.areaType && (
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary">Type: </Text>
                <Text strong>{latest.areaType.name}</Text>
              </div>
            )}

            <div className="dashboard-latest-meta">
              <div>
                <Text type="secondary">Measurement</Text>
                <div>{latest.measurementCode}</div>
              </div>
              <div>
                <Text type="secondary">Taken</Text>
                <div>{formatMeasurementTimestamp(latest.takenAt, latest)}</div>
              </div>
            </div>

            <div className="dashboard-metric-grid">
              {metricsToShow.length === 0 && <Empty description="No metrics for this measurement" />}
              {metricsToShow.map((m) => {
                const resolveVariant = () => {
                  if (m.trend === 'none' || m.trend === 'same') {
                    return 'neutral';
                  }
                  const isUp = m.trend === 'up';
                  const positiveDirection = Boolean(m.positiveIsGood);
                  return isUp === positiveDirection ? 'positive' : 'negative';
                };
                const cardVariant = resolveVariant();

                return (
                  <Card
                    key={m.key}
                    size="small"
                    className={`dashboard-mini-card dashboard-mini-card-trend-${cardVariant}`}
                  >
                    <Text type="secondary" className="mini-label">{m.label}</Text>
                    <div className="mini-value">{m.value}</div>

                    <div
                      className={`mini-trend ${
                        m.trend === 'up'
                          ? 'mini-trend-positive'
                          : m.trend === 'down'
                          ? 'mini-trend-negative'
                          : 'mini-trend-neutral'
                      }`}
                    >
                      <TrendIndicator trend={m.trend as TrendDirection} positiveIsGood={m.positiveIsGood} size="large" />
                    </div>
                  </Card>
                );
              })}
            </div>
            {showReportCta && (canNavigateArea || canNavigateMeasurement) && (
              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={handleCtaClick}>
                  {ctaLabel}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Empty description={emptyDescription} />
      )}
    </Card>
  );
}
